// src/components/editor/EditorRailPreview.jsx
//
// Live preview docked INSIDE the right rail (per the QA prototype). Show Preview
// swaps this panel in where Chapter progress / Theme / Reviewer activity normally
// sit; Hide Preview swaps them back. This is deliberately NOT a modal — the author
// keeps writing with the formatted page visible beside them.
//
// Pagination is free here: the editor already lays the manuscript out as real
// .rv-page sheets (816x1056, Letter @96dpi), so one sheet IS one page. We render
// the current sheet at native size and transform-scale it down to whatever width
// the rail gives us, which keeps both the width AND the length in true proportion.
//
// Format (Physical Book / Kindle / Mobile) changes the device chrome and the page
// tint around that sheet. It does NOT re-flow the text to a different page size —
// the manuscript is paginated to Letter sheets upstream, and re-flowing per device
// is a separate piece of work.
import React, { useMemo, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { DEFAULT_BOOK_SIZE, pageSizePx } from '../../config/bookSizes';
import { splitPages } from '../../utils/manuscriptPages';
import { Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// The page box comes from the book's trim size, not a constant. It used to be
// hardcoded Letter under a "must match --rv-page-width" comment, which stopped
// being true once authors could choose a trim size: a 6 x 9 book was written on
// 6 x 9 sheets and previewed here on 8.5 x 11 ones.

export const PREVIEW_FORMATS = {
  book:   { label: 'Physical Book', frameClass: 'rpv-book' },
  kindle: { label: 'Kindle',        frameClass: 'rpv-kindle' },
  mobile: { label: 'Mobile',        frameClass: 'rpv-mobile' },
};

export default function EditorRailPreview({
  html = '',
  format = 'book',
  onFormatChange,
  scope = 'chapter',
  onScopeChange,
  chapterRange = null,
  onClose,
  trimSize = DEFAULT_BOOK_SIZE,
}) {
  const { width: PAGE_W, height: PAGE_H } = useMemo(() => pageSizePx(trimSize), [trimSize]);
  const allPages = useMemo(() => splitPages(html), [html]);
  const [pageIndex, setPageIndex] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  const fmt = PREVIEW_FORMATS[format] || PREVIEW_FORMATS.book;

  // "This chapter" narrows the pager to the pages the current chapter occupies;
  // "Flip through book" walks the whole manuscript. With no resolvable chapter
  // (an empty document, or no headings yet) the chapter scope just shows
  // everything rather than an empty panel.
  const pages = useMemo(() => {
    if (scope !== 'chapter' || !chapterRange) return allPages;
    return allPages.slice(chapterRange.start, chapterRange.end + 1);
  }, [allPages, scope, chapterRange]);

  const total = pages.length;
  // Clamp rather than reset: the author is typing, and snapping back to page 1 on
  // every keystroke would make the panel unusable.
  const safeIndex = Math.min(pageIndex, Math.max(0, total - 1));

  useEffect(() => {
    if (pageIndex > total - 1) setPageIndex(Math.max(0, total - 1));
  }, [total, pageIndex]);

  // Jump to the front of the new range when the scope changes or the caret moves
  // into a different chapter — keyed on the chapter's first page, not the range
  // itself, so it doesn't fire every time typing grows the chapter by a page.
  const rangeStart = scope === 'chapter' ? chapterRange?.start ?? null : null;
  useEffect(() => { setPageIndex(0); }, [scope, rangeStart]);

  // Scale the native sheet down to whatever width the rail currently gives us.
  const canvasRef = useRef(null);
  const [scale, setScale] = useState(0);
  useLayoutEffect(() => {
    const el = canvasRef.current;
    if (!el) return undefined;
    const recompute = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / PAGE_W);
    };
    recompute();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', recompute);
      return () => window.removeEventListener('resize', recompute);
    }
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
    // PAGE_W is a dependency: changing the trim size changes the page box, and
    // a scale computed against the old width leaves the sheet the wrong size
    // until something else happens to resize the rail.
  }, [PAGE_W]);

  // Close the format menu on any outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = () => setMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpen]);

  return (
    <Box className="rail-preview">
      <Box className="rail-preview-head">
        <Typography component="span" className="rail-preview-title">
          <span className="rail-preview-dot" />
          Live preview
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={onClose}
          className="rail-preview-close"
          title="Hide preview"
          aria-label="Hide preview"
        >
          <CloseIcon sx={{ fontSize: 15 }} />
        </Box>
      </Box>

      <Box className="rail-preview-format">
        <Box
          component="button"
          type="button"
          className="rail-preview-format-btn"
          onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
        >
          <span>Format: {fmt.label}</span>
          <KeyboardArrowDownIcon sx={{ fontSize: 15 }} />
        </Box>
        {menuOpen && (
          <Box className="rail-preview-format-menu">
            {Object.entries(PREVIEW_FORMATS).map(([key, f]) => (
              <Box
                component="button"
                type="button"
                key={key}
                className={`rail-preview-format-item${key === format ? ' active' : ''}`}
                onClick={(e) => { e.stopPropagation(); onFormatChange?.(key); setMenuOpen(false); }}
              >
                {f.label}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box className="rail-preview-scope">
        {[['chapter', 'This chapter'], ['book', 'Flip through book']].map(([key, label]) => (
          <Box
            component="button"
            type="button"
            key={key}
            className={`rail-preview-scope-btn${scope === key ? ' active' : ''}`}
            onClick={() => onScopeChange?.(key)}
          >
            {label}
          </Box>
        ))}
      </Box>

      <Box className="rail-preview-scroll">
        <Box className={`rail-preview-stage ${fmt.frameClass}`}>
          <Box
            ref={canvasRef}
            className="rail-preview-canvas"
            style={{ aspectRatio: `${PAGE_W} / ${PAGE_H}` }}
          >
            {total > 0 && scale > 0 && (
              <Box
                className="rail-preview-page"
                style={{
                  width: PAGE_W,
                  height: PAGE_H,
                  // Re-declared locally: `.rv-page` sizes itself from these and
                  // they are Letter on `:root`, overridden only in the editor.
                  '--rv-page-width': `${PAGE_W}px`,
                  '--rv-page-height': `${PAGE_H}px`,
                  transform: `scale(${scale})`,
                }}
                dangerouslySetInnerHTML={{ __html: pages[safeIndex] }}
              />
            )}
            {total === 0 && (
              <Box className="rail-preview-empty">Nothing written yet.</Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box className="rail-preview-nav">
        <Box
          component="button"
          type="button"
          className="rail-preview-nav-btn"
          disabled={safeIndex <= 0}
          onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
        >
          &larr; Prev
        </Box>
        <Typography component="span" className="rail-preview-count">
          {total === 0
            ? 'No pages'
            : `Page ${safeIndex + 1} / ${total}${scope === 'chapter' && chapterRange ? ' of this chapter' : ''}`}
        </Typography>
        <Box
          component="button"
          type="button"
          className="rail-preview-nav-btn"
          disabled={safeIndex >= total - 1}
          onClick={() => setPageIndex((i) => Math.min(total - 1, i + 1))}
        >
          Next &rarr;
        </Box>
      </Box>
    </Box>
  );
}
